import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    CircularProgress, 
    Chip, 
    Button, 
    TablePagination, 
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import { HousekeepingService } from '../../api/services/housekeepingService';
import LostFoundFormModal from '../../components/LostFoundFormModal';
import ClaimItemModal from '../../components/ClaimItemModal';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import { toast } from '../../utils/toast';
import { extractTotalCount, standardFilterContainerSx, standardFilterInputSx, standardResetButtonSx } from '../../components/common/tableStyles';

export default function LostFoundTab({ tenantId }) {
    const [lostFoundItems, setLostFoundItems] = useState([]);
    const [isLoadingLostFound, setIsLoadingLostFound] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [total, setTotal] = useState(0);
    const [lfFilters, setLfFilters] = useState({ room: '', description: '', dateFound: '' });

    const [lostFoundModal, setLostFoundModal] = useState(false);
    const [claimModal, setClaimModal] = useState({ open: false, item: null });
    const [discardModal, setDiscardModal] = useState({ open: false, item: null });
    const [viewClaimModal, setViewClaimModal] = useState({ open: false, item: null });
    const [isDiscarding, setIsDiscarding] = useState(false);

    const fetchLostFoundItems = async () => {
        setIsLoadingLostFound(true);
        try {
            const params = {
                page,
                limit: rowsPerPage,
                room: lfFilters.room || undefined,
                description: lfFilters.description || undefined,
                dateFound: lfFilters.dateFound || undefined,
            };
            const data = await HousekeepingService.getLostFoundItems(tenantId, params);
            const items = data?.data?.items || data?.items || data?.data || data || [];
            setLostFoundItems(Array.isArray(items) ? items : []);
            setTotal(extractTotalCount(data, items));
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoadingLostFound(false);
        }
    };

    useEffect(() => {
        fetchLostFoundItems();
    }, [tenantId, page, rowsPerPage, lfFilters]);

    const handleDiscardItem = async () => {
        setIsDiscarding(true);
        try {
            await HousekeepingService.discardLostFoundItem(discardModal.item.id, tenantId);
            toast.warning("Lost item successfully marked as discarded");
            setDiscardModal({ open: false, item: null });
            fetchLostFoundItems();
        } catch (error) {
            console.error(error);
            toast.error(error.message || "Failed to discard item");
        } finally {
            setIsDiscarding(false);
        }
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleLfFilterChange = (e) => {
        setLfFilters({ ...lfFilters, [e.target.name]: e.target.value });
        setPage(0);
    };

    const paginatedLostFoundItems = lostFoundItems;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 18 }} />} onClick={() => setLostFoundModal(true)}>
                    Log Found Item
                </Button>
            </Box>

            <Box sx={standardFilterContainerSx}>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField size="small" name="room" value={lfFilters.room} onChange={handleLfFilterChange} placeholder="Room number" sx={standardFilterInputSx} />
                    <TextField size="small" name="description" value={lfFilters.description} onChange={handleLfFilterChange} placeholder="Item description" sx={standardFilterInputSx} />
                    <TextField size="small" type="date" name="dateFound" value={lfFilters.dateFound} onChange={handleLfFilterChange} sx={standardFilterInputSx} />
                    {(lfFilters.room || lfFilters.description || lfFilters.dateFound) && (
                        <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={() => setLfFilters({ room: '', description: '', dateFound: '' })}>Reset</Button>
                    )}
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Item Description</TableCell>
                            <TableCell>Room Found</TableCell>
                            <TableCell>Date Found</TableCell>
                            <TableCell>Found By</TableCell>
                            <TableCell>Storage Location</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoadingLostFound ? (
                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
                        ) : paginatedLostFoundItems.length === 0 ? (
                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>No items logged in the registry.</TableCell></TableRow>
                        ) : (
                            paginatedLostFoundItems.map((item) => (
                                <TableRow key={item.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="700">
                                            {item.description}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="600">
                                            {item.room?.roomNumber || item.roomId}
                                        </Typography>
                                        {(item.room?.type || item.room?.roomType?.name) && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '11px' }}>
                                                {item.room?.type || item.room?.roomType?.name}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{item.dateFound}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {item.foundByStaff?.fullName || item.foundByStaffId}
                                        </Typography>
                                        {(item.foundByStaff?.designation || item.foundByStaff?.employeeId) && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '11px' }}>
                                                {[item.foundByStaff?.designation, item.foundByStaff?.employeeId].filter(Boolean).join(' • ')}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{item.storageLocation || '—'}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={item.status || 'Found'} 
                                            size="small" 
                                            color={item.status === 'Claimed' ? 'success' : (item.status === 'Discarded' ? 'default' : 'warning')} 
                                            sx={{ fontWeight: 600 }}
                                        />
                                        {item.status === 'Claimed' && item.claim?.claimedBy && (
                                            <Typography variant="caption" sx={{ display: 'block', color: 'success.main', fontWeight: 500, fontSize: '11px', mt: 0.5 }}>
                                                Claimant: {item.claim.claimedBy}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {item.status === 'Claimed' ? (
                                            <Button 
                                                size="small" 
                                                variant="outlined" 
                                                color="primary" 
                                                onClick={() => setViewClaimModal({ open: true, item })}
                                                startIcon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
                                                sx={{ textTransform: 'none', borderRadius: 1.5, py: 0.25, px: 1, fontSize: '12px' }}
                                            >
                                                Claim Details
                                            </Button>
                                        ) : item.status === 'Discarded' ? (
                                            <Chip size="small" label="Discarded" variant="outlined" sx={{ color: '#94a3b8' }} />
                                        ) : (
                                            <>
                                                <Button size="small" color="primary" variant="outlined" onClick={() => setClaimModal({ open: true, item })} sx={{ mr: 1, textTransform: 'none', borderRadius: 1.5 }}>Claim Item</Button>
                                                <Button size="small" color="error" variant="outlined" onClick={() => setDiscardModal({ open: true, item })} sx={{ textTransform: 'none', borderRadius: 1.5 }}>Discard</Button>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={lostFoundItems.length === 0 ? 0 : total}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>

            <LostFoundFormModal open={lostFoundModal} onClose={() => setLostFoundModal(false)} onSuccess={fetchLostFoundItems} />
            <ClaimItemModal open={claimModal.open} item={claimModal.item} onClose={() => setClaimModal({ open: false, item: null })} onSuccess={fetchLostFoundItems} />
            <ConfirmDeleteModal 
                open={discardModal.open}
                onClose={() => setDiscardModal({ open: false, item: null })}
                onConfirm={handleDiscardItem}
                isLoading={isDiscarding}
                title="Confirm Discard Item"
                bodyText="Are you sure you want to discard this item? This will officially mark the belonging as disposed of or removed from inventory records. This action cannot be undone."
                confirmText="Discard Item"
                confirmColor="error"
            />

            {/* View Claim Details Dialog */}
            <Dialog 
                open={viewClaimModal.open} 
                onClose={() => setViewClaimModal({ open: false, item: null })}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Typography variant="h6" fontWeight="bold">Claim Details</Typography>
                    <IconButton size="small" onClick={() => setViewClaimModal({ open: false, item: null })}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {viewClaimModal.item?.claim ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Item</Typography>
                                <Typography variant="body2" fontWeight="600">{viewClaimModal.item.description} (Room {viewClaimModal.item.room?.roomNumber || viewClaimModal.item.roomId})</Typography>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography variant="caption" color="text.secondary">Claimed By</Typography>
                                <Typography variant="body2" fontWeight="600">{viewClaimModal.item.claim.claimedBy || '—'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Claimant Contact</Typography>
                                <Typography variant="body2" fontWeight="600">{viewClaimModal.item.claim.claimedContact || '—'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">ID Proof</Typography>
                                <Typography variant="body2" fontWeight="600">
                                    {[viewClaimModal.item.claim.idProofType, viewClaimModal.item.claim.idProofNumber].filter(Boolean).join(': ') || '—'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Claim Date</Typography>
                                <Typography variant="body2">
                                    {viewClaimModal.item.claim.claimDate ? new Date(viewClaimModal.item.claim.claimDate).toLocaleString() : '—'}
                                </Typography>
                            </Box>
                            {viewClaimModal.item.claim.notes && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                                    <Typography variant="body2">{viewClaimModal.item.claim.notes}</Typography>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Typography color="text.secondary" variant="body2">No claim details available for this item.</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button variant="contained" onClick={() => setViewClaimModal({ open: false, item: null })} sx={{ borderRadius: 2 }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
